import api from '../utils/axios';

// Types
export interface OwnerMetrics {
  totalTenants: number;
  activeTenants: number;
  monthlyRevenue: number;
  totalStorageUsed: number;
  activeIssues: number;
  systemHealth?: {
    cpuUsage: number;
    memoryUsage: number;
    uptime: number;
  };
}

export interface TenantSummary {
  id: string;
  name: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'CANCELLED';
  package: 'BASIC' | 'PROFESSIONAL' | 'ENTERPRISE';
  storageUsed: number;
  storageLimit: number;
  userLimit: number;
  createdAt: string;
  updatedAt: string;
  _count: {
    users: number;
    claims: number;
    clients: number;
  };
  billing?: {
    status: string;
    monthlyRate: number;
    nextBilling: string;
  };
}

export interface TenantDetails extends TenantSummary {
  users: Array<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    created_at: string;
  }>;
  usage: Array<{
    id: string;
    date: string;
    claims: number;
    users: number;
    storage: number;
    apiCalls: number;
  }>;
}

export interface SystemHealth {
  current: {
    cpuUsage: number;
    memoryUsage: number;
    diskUsage: number;
    uptime: number;
    timestamp: string;
  } | null;
  history: Array<{
    cpuUsage: number;
    memoryUsage: number;
    diskUsage: number;
    uptime: number;
    timestamp: string;
  }>;
  alerts: Array<{
    id: string;
    type: 'ERROR' | 'WARNING' | 'INFO';
    message: string;
    resolved: boolean;
    createdAt: string;
  }>;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  amount: number;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  dueDate: string;
  paidAt?: string;
  createdAt: string;
  billing: {
    tenant: {
      id: string;
      name: string;
    };
  };
  payments: Array<{
    id: string;
    amount: number;
    method: string;
    status: string;
    createdAt: string;
  }>;
}

export interface BillingSummary {
  monthlyRevenue: number;
  pendingAmount: number;
  overdueAmount: number;
  activeSubscriptions: number;
}

export interface UsageAnalytics {
  id: string;
  date: string;
  claims: number;
  users: number;
  storage: number;
  apiCalls: number;
  tenant: {
    name: string;
  };
}

export interface GrowthAnalytics {
  tenants: {
    thisMonth: number;
    lastMonth: number;
    growth: number;
  };
  revenue: {
    thisMonth: number;
    lastMonth: number;
    growth: number;
  };
}

// API Service Class
export class OwnerService {
  // Dashboard
  async getDashboardMetrics(): Promise<{
    metrics: OwnerMetrics;
    recentActivity: Array<{
      id: string;
      name: string;
      createdAt: string;
      status: string;
      package: string;
    }>;
  }> {
    const response = await api.get('/owner/dashboard');
    return response.data as {
      metrics: OwnerMetrics;
      recentActivity: Array<{
        id: string;
        name: string;
        createdAt: string;
        status: string;
        package: string;
      }>;
    };
  }

  // Client Management
  async getTenants(params: {
    page?: number;
    limit?: number;
    status?: string;
    package?: string;
    search?: string;
  } = {}): Promise<{
    tenants: TenantSummary[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const response = await api.get('/owner/clients', { params });
    return response.data as {
      tenants: TenantSummary[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    };
  }

  async getTenant(id: string): Promise<TenantDetails> {
    const response = await api.get(`/owner/clients/${id}`);
    return response.data as TenantDetails;
  }

  async createTenant(data: {
    name: string;
    email: string;
    password: string;
    package?: string;
    storageLimit?: number;
    userLimit?: number;
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
  }): Promise<{
    message: string;
    tenant: TenantSummary;
    adminUser: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
    };
  }> {
    const response = await api.post('/owner/clients', data);
    return response.data as {
      message: string;
      tenant: TenantSummary;
      adminUser: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        role: string;
      };
    };
  }

  async updateTenant(id: string, data: {
    status?: string;
    package?: string;
    storageLimit?: number;
    userLimit?: number;
  }): Promise<TenantSummary> {
    const response = await api.put(`/owner/clients/${id}`, data);
    return response.data as TenantSummary;
  }

  // Billing
  async getBilling(params: {
    page?: number;
    limit?: number;
    status?: string;
  } = {}): Promise<{
    invoices: Invoice[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const response = await api.get('/owner/billing', { params });
    return response.data as {
      invoices: Invoice[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    };
  }

  async getBillingSummary(): Promise<BillingSummary> {
    const response = await api.get('/owner/billing/summary');
    return response.data as BillingSummary;
  }

  // System Health
  async getSystemHealth(): Promise<SystemHealth> {
    const response = await api.get('/owner/system/health');
    return response.data as SystemHealth;
  }

  async createAlert(alert: {
    type: 'ERROR' | 'WARNING' | 'INFO';
    message: string;
  }): Promise<{ id: string; type: string; message: string; resolved: boolean; createdAt: string }> {
    const response = await api.post('/owner/system/alerts', alert);
    return response.data as { id: string; type: string; message: string; resolved: boolean; createdAt: string };
  }

  async resolveAlert(id: string): Promise<{ id: string; resolved: boolean }> {
    const response = await api.put(`/owner/system/alerts/${id}/resolve`);
    return response.data as { id: string; resolved: boolean };
  }

  // Analytics
  async getUsageAnalytics(period: '7d' | '30d' | '90d' = '30d'): Promise<UsageAnalytics[]> {
    const response = await api.get('/owner/analytics/usage', { params: { period } });
    return response.data as UsageAnalytics[];
  }

  async getGrowthAnalytics(): Promise<GrowthAnalytics> {
    const response = await api.get('/owner/analytics/growth');
    return response.data as GrowthAnalytics;
  }
}

// Export singleton instance
export const ownerService = new OwnerService(); 